import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import api from "../../services/api";

const PAGE_OPTIONS = [
  { key: "privacy_policy", label: "Privacy Policy" },
  { key: "terms_conditions", label: "Terms & Conditions" },
  { key: "legal_notice", label: "Legal Notice" },
];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "fr", label: "French" },
  // add more languages as needed
];

export default function LegalPagesForm() {
  const [pageKey, setPageKey] = useState("privacy_policy");
  const [language, setLanguage] = useState("en");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "info", message: "" });

  // Fetch existing page content
  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await api.get(`/rockstar/legal-page?key=${pageKey}&lang=${language}`);
        const translation = res.data?.translations?.[0] || {};
        setTitle(translation.title || "");
        setContent(translation.content || "");
      } catch (err) {
        setTitle("");
        setContent("");
        console.error("fetch page failed", err);
      }
    };
    fetchPage();
  }, [pageKey, language]);

  const handleSave = async () => {
    if (!content.trim()) {
      setSnack({ open: true, severity: "error", message: "Content cannot be empty" });
      return;
    }
    setLoading(true);
    try {
      await api.post("/rockstar/admin/legal-page", {
        key: pageKey,
        language,
        title,
        content,
      });
      setSnack({ open: true, severity: "success", message: "Page updated successfully" });
    } catch (err) {
      console.error("save failed", err);
      setSnack({ open: true, severity: "error", message: "Failed to save" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: "0 8px 30px rgba(0,0,0,0.15)" }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Manage Legal Pages
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Update Privacy Policy, Terms & Conditions, and Legal Notice dynamically with translations.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="page-label">Page</InputLabel>
              <Select
                labelId="page-label"
                value={pageKey}
                label="Page"
                onChange={(e) => setPageKey(e.target.value)}
              >
                {PAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="lang-label">Language</InputLabel>
              <Select
                labelId="lang-label"
                value={language}
                label="Language"
                onChange={(e) => setLanguage(e.target.value)}
              >
                {LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Content"
          fullWidth
          multiline
          rows={8}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />

        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Save
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Container>
  );
}
